/**
 * Zone Mask Extraction Utilities
 *
 * Extracts color-based zones from a mask image for multi-zone masking
 */

import { hexToRgb } from './colorUtils';
import { supportsWebP } from '../../../utilities/webpDetection';

const imageType = supportsWebP() ? 'image/webp' : 'image/png';

/**
 * Extract a color zone from a mask image
 * Creates a new mask image where only pixels matching the target color are visible
 *
 * @param maskImage - The source mask image
 * @param targetColorHex - Hex color to extract (e.g., "#FF0000")
 * @param colorTolerance - RGB tolerance for color matching (default: 10)
 * @returns Promise resolving to a data URL of the extracted zone mask
 */
export async function extractColorZone(
  maskImage: HTMLImageElement,
  targetColorHex: string,
  colorTolerance: number = 10
): Promise<string> {
  // Create a canvas to process the mask image
  const canvas = document.createElement('canvas');
  canvas.width = maskImage.width;
  canvas.height = maskImage.height;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context for zone extraction');
  }

  // Draw the mask image onto the canvas
  ctx.drawImage(maskImage, 0, 0);

  // Get image data
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  // Convert target color to RGB
  const targetRgb = hexToRgb(targetColorHex);

  // Process each pixel
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    // Skip transparent pixels
    if (a === 0) {
      continue;
    }

    // Calculate Euclidean distance in RGB space for better color matching
    // This handles anti-aliasing and color variations better than component-wise comparison
    const rDiff = r - targetRgb.r;
    const gDiff = g - targetRgb.g;
    const bDiff = b - targetRgb.b;
    const colorDistance = Math.sqrt(rDiff * rDiff + gDiff * gDiff + bDiff * bDiff);

    // Use a tolerance threshold based on Euclidean distance
    // For RGB space, max distance is ~441 (sqrt(255^2 + 255^2 + 255^2))
    // A tolerance of 30-50 works well for anti-aliased edges
    const maxColorDistance = colorTolerance * 1.5; // Scale tolerance for 3D space
    const matchesColor = colorDistance <= maxColorDistance;

    if (matchesColor) {
      // Convert matching pixels to black (required for destination-in composite operation)
      // The mask expects black shapes with transparent background
      data[i] = 0; // Red = 0 (black)
      data[i + 1] = 0; // Green = 0 (black)
      data[i + 2] = 0; // Blue = 0 (black)
      data[i + 3] = 255; // Alpha = 255 (fully opaque)
    } else {
      // Make pixel fully transparent (not part of this zone)
      data[i] = 0;
      data[i + 1] = 0;
      data[i + 2] = 0;
      data[i + 3] = 0; // Alpha = 0 (fully transparent)
    }
  }

  // Put the processed image data back
  ctx.putImageData(imageData, 0, 0);

  // Convert to data URL
  return canvas.toDataURL(imageType);
}

/**
 * Create a blob URL from a data URL for use in CSS masks
 *
 * @param dataUrl - Data URL of the extracted zone mask
 * @returns Blob URL
 */
export async function dataUrlToBlobUrl(dataUrl: string): Promise<string> {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * Extract zone mask and return as blob URL
 * Convenience function that combines extraction and blob URL creation
 *
 * @param maskImage - The source mask image
 * @param targetColorHex - Hex color to extract
 * @param colorTolerance - RGB tolerance for color matching
 * @returns Promise resolving to a blob URL of the extracted zone mask
 */
export async function extractZoneMaskAsBlobUrl(
  maskImage: HTMLImageElement,
  targetColorHex: string,
  colorTolerance: number = 10
): Promise<string> {
  const dataUrl = await extractColorZone(maskImage, targetColorHex, colorTolerance);
  return dataUrlToBlobUrl(dataUrl);
}
